import React, { useState,useEffect } from 'react';
import DatePicker from 'react-datepicker';
import TextEditor from '@components/form-elements/TextEditor';
import { fetchJson } from '@utils/fetchJson';
import { API_BASE_URL } from '../../../config';
import { X } from 'lucide-react';
const parseTimeInput = (input) => {
  const cleaned = autoFormatTimeInput(input);
  const timeRegex = /(?:(\d+)w)?\s*(?:(\d+)d)?\s*(?:(\d+)h)?\s*(?:(\d+)m)?/i;
  const match = cleaned.match(timeRegex);

  return {
    week: Number(match?.[1]) || 0,
    day: Number(match?.[2]) || 0,
    hours: Number(match?.[3]) || 0,
    minute: Number(match?.[4]) || 0,
  };
};
const stringifyTime = ({ week = 0, day = 0, hours = 0, minute = 0 }) => {
  let result = '';
  if (week) result += `${week}w `;
  if (day) result += `${day}d `;
  if (hours) result += `${hours}h `;
  if (minute) result += `${minute}m`;
  return result.trim();
};
const getTotalMinutes = ({ week = 0, day = 0, hours = 0, minute = 0 }) => {
  return week * 7 * 24 * 60 + day * 24 * 60 + hours * 60 + minute;
};
const autoFormatTimeInput = (input) => {
  return input
    .toLowerCase()
    .replace(/(hours|hour|hrs|hr)/g, 'h')
    .replace(/(minutes|minute|mins|min)/g, 'm')
    .replace(/(days|day|d)(?!\w)/g, 'd')
    .replace(/(weeks|week|w)(?!\w)/g, 'w')
    .replace(/(\d+)([wdhm])/g, '$1$2 ') // inserts space after each unit
    .replace(/\s+/g, ' ')               // normalize multiple spaces
    .trim();
};
const WorkLogForm = ({ taskId = null, onClose, onSaved, initialData = null }) => {
  const [timeSpent, setTimeSpent] = useState('');
  const [logDate, setLogDate] = useState(new Date());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [loggedTimeDisplay, setLoggedTimeDisplay] = useState('');

  useEffect(() => {
  const parsed = parseTimeInput(timeSpent);
  const totalMin = getTotalMinutes(parsed);
  if (totalMin > 0) {
    const readable = stringifyTime(parsed);
    setLoggedTimeDisplay(`${readable} logged`);
  } else {
    setLoggedTimeDisplay('0m logged');
  }
}, [timeSpent]);

  const handleSubmit = async () => {
    if (!taskId) return alert('Task is required to log time');
    const parsedTime = parseTimeInput(timeSpent);
    if (parsedTime.day === 0 && parsedTime.hours === 0 && parsedTime.minute === 0) return alert('Time must be greater than zero');

    setSaving(true);
    try {
      const res = await fetchJson(`${API_BASE_URL}/taskMaster/timeEstimation`, {
        method: 'POST',
        body: JSON.stringify({
          taskID: taskId,
          type: 'put',
          EstLog: JSON.stringify(parsedTime),
          note,
          log_date: logDate.toLocaleDateString('en-GB'),
        }),
      });
      if (res?.flag === 'S') {
        onSaved?.();
      } else {
        alert('Failed to save work log');
      }
    } catch (err) {
      console.error('Error saving work log:', err);
      alert('Server error while saving');
    }
    setSaving(false);
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-lg rounded shadow-lg p-6 relative">
        <h2 className="text-lg font-semibold mb-4">Time tracking</h2>
        <div className="text-sm text-blue-600 mb-2 animate-pulse">{loggedTimeDisplay}</div>
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="text-sm font-medium block mb-1">Time spent</label>
            <input
              type="text"
              className="ws-input form-input w-full text-gray-600 text-md bg-gray-100 rounded focus:outline-none text-sm px-3 py-2 pr-10 rounded"
              placeholder="e.g. 2w 4d 6h 45m"
              value={timeSpent}
             onChange={(e) => {
  const formatted = autoFormatTimeInput(e.target.value);
  setTimeSpent(formatted);
}}
            />
          </div>
          <div className="flex-1">
            <label className="text-sm font-medium block mb-1">Date started</label>
            <DatePicker
                selected={logDate}
                onChange={(date) => setLogDate(date)}
                className="ws-input form-input w-full text-gray-600 text-md bg-gray-100 rounded focus:outline-none text-sm px-3 py-2 pr-10 rounded"
                dateFormat="dd/MM/yyyy"
                showTimeSelect
            />
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Use the format: <code>2w 4d 6h 45m</code> OR <code>4d 6h 45m</code> OR <code>4d 6h 45m</code><br />
          • w = weeks<br />
          • d = days<br />
          • h = hours<br />
          • m = minutes
        </p>
        <div className="mb-4">
          <label className="text-sm font-medium block mb-1">Work Details</label>
          <TextEditor value={note} showEditorInitially={true} onChange={setNote} />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded"
            disabled={saving}
          >Cancel</button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkLogForm;